import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  GearSix, Storefront, ShareNetwork, Sparkle, Books, Megaphone, Package,
  Cpu, Database, Lightning, ArrowClockwise, HardDrives,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { KpiCard } from "../components/KpiCard";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge, StatusDot } from "../components/StatusBadge";
import { DataTable } from "../components/DataTable";
import { ConnectionHealth } from "../components/ConnectionHealth";
import { useSettingsData } from "../usePromo";
import { MARKETPLACES, SOCIAL_PLATFORMS, PLATFORM_BY_KEY } from "../platforms";
import { fmtDateTime, fmtRelative } from "../campaignUtils";

export default function PromotionSettings() {
  const { t } = useTranslation();
  const s = useSettingsData();

  const d = useMemo(() => {
    const backendOk = s.health?.status === "ok";
    const activePromos = s.promotions.filter((p) => p.status === "active");
    const schedulerActive = activePromos.some((p) => p.next_run_at && new Date(p.next_run_at) >= new Date());
    const scheduled = s.posts.filter((p) => ["scheduled", "pending"].includes(p.status)).length;
    const publishing = s.posts.filter((p) => ["processing", "publishing"].includes(p.status)).length;
    const lastActivity = s.events[0]?.created_at || null;

    const mkConnected = MARKETPLACES.filter((mk) => s.sources.some((x) => x.connector_id === mk.key && x.status === "connected")).length;
    const socialPlatforms = [...new Set(s.accounts.map((a) => a.platform).filter(Boolean))];
    const scConnected = new Set(s.accounts.filter((a) => a.status === "connected").map((a) => a.platform)).size;
    const activeProviders = [...new Set(s.promotions.map((p) => p.ai?.provider).filter(Boolean))];

    return {
      backendOk, activePromos, schedulerActive, scheduled, publishing, lastActivity,
      mkConnected, socialPlatforms, scConnected, activeProviders,
      products: s.stats?.products ?? null,
    };
  }, [s]);

  // Section 2 — marketplace rows (only real connectors present in sources).
  const marketplaceRows = useMemo(() => {
    const byPlatform = Object.fromEntries((s.stats?.by_platform || []).map((x) => [x.platform || x._id, x.products ?? x.count]));
    return s.sources.map((src) => {
      const mk = MARKETPLACES.find((m) => m.key === src.connector_id);
      return {
        id: src.id,
        key: src.connector_id,
        label: mk?.label || src.connector_id,
        name: src.name,
        Icon: mk?.Icon || Storefront,
        status: src.status,
        lastSync: src.last_sync_at || null,
        products: byPlatform[src.connector_id] ?? null,
      };
    });
  }, [s.sources, s.stats]);

  // Section 3 — social platform rows (only configured platforms).
  const socialRows = useMemo(() => d.socialPlatforms.map((key) => {
    const accts = s.accounts.filter((a) => a.platform === key);
    const connected = accts.filter((a) => a.status === "connected").length;
    return {
      id: key,
      key,
      label: PLATFORM_BY_KEY[key]?.label || key,
      Icon: PLATFORM_BY_KEY[key]?.Icon || ShareNetwork,
      total: accts.length,
      connected,
      status: connected > 0 ? "connected" : "disconnected",
      handles: accts.map((a) => a.handle).filter(Boolean),
    };
  }), [d.socialPlatforms, s.accounts]);

  if (!s.loading && !s.promotions.length && !s.sources.length && !s.accounts.length && !s.providers.length) {
    return (
      <div className="space-y-8" data-testid="settings-page">
        <PromoPageHeader overline={t("promo.nav.settings")} title={t("promo.settings.title")} description={t("promo.settings.subtitle")} />
        <EmptyState Icon={GearSix} title={t("promo.settings.emptyTitle")} description={t("promo.settings.emptyDesc")} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      <PromoPageHeader
        overline={t("promo.nav.settings")}
        title={t("promo.settings.title")}
        description={t("promo.settings.subtitle")}
        actions={
          <button onClick={s.reload} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="settings-refresh">
            <ArrowClockwise size={14} className={s.loading ? "animate-spin" : ""} /> {t("promo.conn.refresh")}
          </button>
        }
      />

      {/* 6 KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="settings-kpis">
        <KpiCard index={0} testId="set-kpi-marketplaces" label={t("promo.settings.kpi.marketplaces")} value={d.mkConnected} Icon={Storefront} />
        <KpiCard index={1} testId="set-kpi-social" label={t("promo.settings.kpi.social")} value={d.scConnected} Icon={ShareNetwork} />
        <KpiCard index={2} testId="set-kpi-templates" label={t("promo.settings.kpi.templates")} value={s.prompts.length} Icon={Sparkle} />
        <KpiCard index={3} testId="set-kpi-kbs" label={t("promo.settings.kpi.kbs")} value={s.kbs.length} Icon={Books} />
        <KpiCard index={4} testId="set-kpi-campaigns" label={t("promo.settings.kpi.campaigns")} value={s.promotions.length} Icon={Megaphone} hint={t("promo.settings.activeHint", { n: d.activePromos.length })} />
        <KpiCard index={5} testId="set-kpi-products" label={t("promo.settings.kpi.products")} value={d.products ?? "—"} Icon={Package} />
      </div>

      {/* Section 1 — Promotion Engine */}
      <Section title={t("promo.settings.sec.engine")} testId="settings-engine">
        <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-card p-5 sm:grid-cols-2 lg:grid-cols-4">
          <InfoRow label={t("promo.settings.engine.status")} value={<StatusBadge status={d.backendOk ? "active" : "error"} label={d.backendOk ? t("promo.settings.val.operational") : t("promo.settings.val.down")} />} />
          {s.health?.version && <InfoRow label={t("promo.settings.engine.version")} value={<span className="font-mono text-sm">{s.health.version}</span>} />}
          <InfoRow label={t("promo.settings.engine.scheduler")} value={<StatusBadge status={d.schedulerActive ? "active" : "draft"} label={d.schedulerActive ? t("promo.settings.val.active") : t("promo.settings.val.idle")} />} />
          <InfoRow label={t("promo.settings.engine.queue")} value={<span className="text-sm">{t("promo.settings.queueVal", { s: d.scheduled, p: d.publishing })}</span>} />
          {d.lastActivity && <InfoRow label={t("promo.settings.engine.lastActivity")} value={<span className="text-sm">{fmtRelative(d.lastActivity)} · {fmtDateTime(d.lastActivity)}</span>} />}
        </div>
      </Section>

      {/* Section 2 — Marketplace Connections */}
      <Section title={t("promo.settings.sec.marketplaces")} testId="settings-marketplaces">
        {marketplaceRows.length === 0 ? (
          <EmptyState Icon={Storefront} title={t("promo.settings.noMarketplaces")} />
        ) : (
          <DataTable
            testId="settings-marketplace-table"
            rowKey="id"
            rows={marketplaceRows}
            columns={[
              { key: "mk", label: t("promo.settings.col.marketplace"), render: (r) => <span className="flex items-center gap-2 font-medium"><r.Icon size={14} /> {r.label}</span> },
              { key: "name", label: t("promo.settings.col.account"), render: (r) => r.name || "—" },
              { key: "status", label: t("promo.settings.col.status"), render: (r) => <StatusBadge status={r.status} label={t(`promo.conn.${r.status === "connected" ? "connected" : "disconnected"}`)} /> },
              { key: "sync", label: t("promo.settings.col.lastSync"), render: (r) => r.lastSync ? fmtDateTime(r.lastSync) : "—" },
              { key: "products", label: t("promo.settings.col.products"), align: "right", render: (r) => r.products != null ? <span className="tabular-nums">{r.products}</span> : "—" },
            ]}
          />
        )}
      </Section>

      {/* Section 3 — Social Platforms */}
      <Section title={t("promo.settings.sec.social")} testId="settings-social">
        {socialRows.length === 0 ? (
          <EmptyState Icon={ShareNetwork} title={t("promo.settings.noSocial")} />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="settings-social-grid">
            {socialRows.map((r, i) => (
              <motion.div key={r.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }}
                className="rounded-lg border border-border bg-card p-4" data-testid={`settings-social-${r.key}`}>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-accent/40"><r.Icon size={15} /></span>
                  <span className="text-sm font-medium">{r.label}</span>
                  <span className="ml-auto"><StatusBadge status={r.status} label={t(`promo.conn.${r.status === "connected" ? "connected" : "disconnected"}`)} /></span>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{t("promo.settings.social.accounts", { n: r.total })}</span>
                  <span>{t("promo.settings.social.connected", { n: r.connected })}</span>
                </div>
                {r.handles.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.handles.slice(0, 4).map((h, hi) => (
                      <span key={`${h}-${hi}`} className="truncate rounded-full border border-border bg-accent/30 px-2 py-0.5 font-mono text-[10px] text-muted-foreground">{h}</span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </Section>

      {/* Section 4 — AI Configuration */}
      <Section title={t("promo.settings.sec.ai")} testId="settings-ai">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-5 lg:col-span-2">
            <div className="promo-overline mb-3">{t("promo.settings.ai.providers")}</div>
            {s.providers.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("promo.templates.noProviders")}</p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {s.providers.map((p) => {
                  const active = d.activeProviders.includes(p.provider);
                  return (
                    <div key={p.id || p.provider} className="flex items-center gap-2 rounded-md border border-border bg-accent/20 px-3 py-2">
                      <Cpu size={15} className="text-muted-foreground" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-medium capitalize">{p.provider}{active && <StatusDot status="active" />}</div>
                        {p.model && <div className="truncate font-mono text-[11px] text-muted-foreground">{p.model}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {d.activeProviders.length > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">{t("promo.settings.ai.activeProvider")}: <span className="font-medium capitalize text-foreground">{d.activeProviders.join(", ")}</span></p>
            )}
          </div>
          <div className="space-y-3">
            <MiniStat Icon={Books} label={t("promo.settings.kpi.kbs")} value={s.kbs.length} />
            <MiniStat Icon={Sparkle} label={t("promo.settings.ai.prompts")} value={s.prompts.length} />
          </div>
        </div>
      </Section>

      {/* Section 5 — System Health */}
      <Section title={t("promo.settings.sec.system")} testId="settings-system">
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-card p-5 sm:grid-cols-3 lg:grid-cols-6" data-testid="settings-system-grid">
          <HealthItem Icon={HardDrives} label={t("promo.settings.sys.backend")} ok={d.backendOk} t={t} />
          <HealthItem Icon={Database} label={t("promo.settings.sys.database")} ok={d.backendOk} t={t} />
          <HealthItem Icon={Lightning} label={t("promo.settings.sys.scheduler")} ok={d.schedulerActive} idleLabel t={t} />
          <HealthItem Icon={Storefront} label={t("promo.settings.sys.marketplace")} ok={d.mkConnected > 0} t={t} />
          <HealthItem Icon={ShareNetwork} label={t("promo.settings.sys.social")} ok={d.scConnected > 0} t={t} />
          <HealthItem Icon={Cpu} label={t("promo.settings.sys.ai")} ok={s.providers.length > 0} t={t} />
        </div>
        <ConnectionHealth title={t("promo.conn.title")} />
      </Section>

      {/* Section 6 — Configuration Summary */}
      <Section title={t("promo.settings.sec.summary")} testId="settings-summary">
        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCell label={t("promo.settings.kpi.marketplaces")} value={d.mkConnected} />
          <SummaryCell label={t("promo.settings.kpi.social")} value={d.scConnected} />
          <SummaryCell label={t("promo.settings.kpi.templates")} value={s.prompts.length} />
          <SummaryCell label={t("promo.settings.kpi.kbs")} value={s.kbs.length} />
          <SummaryCell label={t("promo.settings.kpi.campaigns")} value={s.promotions.length} />
          <SummaryCell label={t("promo.settings.kpi.products")} value={d.products ?? "—"} />
          <SummaryCell label={t("promo.settings.ai.providers")} value={s.providers.length} />
          <SummaryCell label={t("promo.settings.engine.status")} value={<StatusBadge status={d.backendOk ? "active" : "error"} label={d.backendOk ? t("promo.settings.val.operational") : t("promo.settings.val.down")} />} />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, testId, children }) {
  return (
    <section className="space-y-4" data-testid={testId}>
      <h3 className="text-lg font-medium tracking-tight">{title}</h3>
      {children}
    </section>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="space-y-1.5">
      <div className="promo-overline">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function MiniStat({ Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
      <span className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-accent/40"><Icon size={16} /></span>
      <div>
        <div className="text-2xl font-medium tabular-nums leading-none">{value}</div>
        <div className="promo-overline mt-1">{label}</div>
      </div>
    </div>
  );
}

function HealthItem({ Icon, label, ok, idleLabel, t }) {
  return (
    <div className="flex flex-col items-center gap-2 text-center" data-testid={`health-${label}`}>
      <span className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-accent/40"><Icon size={18} /></span>
      <span className="text-xs font-medium">{label}</span>
      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <StatusDot status={ok ? "connected" : "disconnected"} />
        {ok ? t("promo.settings.val.healthy") : (idleLabel ? t("promo.settings.val.idle") : t("promo.conn.disconnected"))}
      </span>
    </div>
  );
}

function SummaryCell({ label, value }) {
  return (
    <div className="bg-card p-4">
      <div className="promo-overline">{label}</div>
      <div className="mt-2 text-xl font-medium tabular-nums">{value}</div>
    </div>
  );
}
